import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BehaviorSubject, filter, map } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { ModifyIssueDialogComponent } from './modify-issue-dialog/modify-issue-dialog.component';
import { HelpService } from '../../../services/help.service';
import { AnalysisManager } from '../../../managers/analysis.manager';
import { Issue } from 'src/app/models/ui/issue.model';
import { BaseComponent } from '../../base.component';
import { NotificationManager } from '../../../managers/notification.manager';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { UserConfigsService } from '@services/user-configs.service';

@Component({
    selector: 'lex-facts-and-issues',
    templateUrl: './facts-and-issues.component.html',
    styleUrls: ['./facts-and-issues.component.scss'],
    standalone: false
})
export class FactsAndIssuesComponent extends BaseComponent implements OnInit {
	public issues: Issue[] = [];
	public selectedIssue: Issue = null;
	public selectedIssueId: string;
	public hoverFieldId: string = null;
	public searchText: string = '';
	public showSelect: boolean = false;
	public searchTerm = new BehaviorSubject<string>('');

	@ViewChild('leftScroll') leftScroll: CdkVirtualScrollViewport;

	constructor(
		public userConfigsService: UserConfigsService,
		private dialog: MatDialog,
		private helpService: HelpService,
		private analysisManager: AnalysisManager,
		private notificationManager: NotificationManager
	) {
		super();
	}

	ngOnInit(): void {
		this.analysisManager.filteredIssues$.pipe(
			filter((data) => !!data),
			tap((data) => {
				this.issues = data.items;
				if (this.selectedIssueId) {
					this.selectedIssue = this.issues.find(x => x.id === this.selectedIssueId);
					if (this.selectedIssue)
						setTimeout(() => this.leftScroll.scrollToIndex(this.issues.indexOf(this.selectedIssue)));
				} else {
					this.selectedIssue = null;
				}

				if (this.issues.length > 0) {
					this.showSelect = true;
				}
			}),
			map(() => null)
		).subscribe();

		this.searchTerm.pipe(
			debounceTime(300),
			distinctUntilChanged(),
			tap((term) => {
				this.searchText = term;
			}),
			tap(() => this.refreshIssueList(this.selectedIssue?.id)),
			takeUntil(this.destroy$)
		).subscribe();

		this.helpService.updateHelpLink('analysis-menu/facts-and-issues');
	}

	public selectIssue(issue: Issue) {
		this.selectedIssue = issue;
		this.analysisManager.setSelectedIssue(issue);
	}

	public addOrEditIssue(issue?: Issue) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = issue;
		this.dialog.open(ModifyIssueDialogComponent, dialogConfig).afterClosed().pipe(
			tap(() => this.refreshIssueList())
		).subscribe();
	}

	public deleteIssue(issue: Issue): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Issue`,
			message: `This issue will be PERMANENTLY DELETED from the entire case.
          <br/> This action cannot be undone.`
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((okClicked) => {
			if (okClicked) {
				this.analysisManager.deleteIssue({issue: issue}).subscribe(() => {
					this.refreshIssueList();
					this.notificationManager.displaySnackBarMessage({ message: 'Issue deleted successfully.' });
				});
			}
		});
	}

	public refreshIssueList(selectIssueId?: string): void {
		this.selectedIssueId = selectIssueId;
		this.analysisManager.refreshFilteredIssues({searchTerm: this.searchText});
	}
}
